import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  type AuthError,
} from 'firebase/auth';
import { LocaleSwitcher } from '@/components/LocaleSwitcher';
import { PhoneEmailInput, type IdentityValue } from '@/components/PhoneEmailInput';
import { authApi, otpErrorKey, type SendOtpRequest } from '@/lib/api/auth';
import { isEmailLike, isPhoneLike } from '@/lib/api-client';
import { auth } from '@/lib/firebase';
import { cn } from '@/lib/cn';
import { EMAIL_OTP_ENABLED } from '@/lib/supported-countries';

/**
 * Welcome screen — single CTA for sign-up + sign-in.
 *
 * If the entered phone/email matches an existing user, the api just
 * sends them a code. If not, the api creates the user lazily and sends
 * the code. The client doesn't need to know which case it was.
 */
export function Welcome(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [identity, setIdentity] = useState<IdentityValue>({ kind: 'phone', value: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    setBusy(true);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      await signInWithPopup(auth, provider);
      // onAuthStateChanged in useAuthBootstrap detects the new user;
      // /auth/me auto-onboards them into Postgres; router lands them on Home.
    } catch (err) {
      const code = (err as AuthError).code;
      // Popup closed/cancelled by the user — not an error.
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        // In-app browsers (Telegram, Instagram, …) frequently block or can't
        // return an auth popup. Fall back to a full-page redirect, which they
        // do support — onAuthStateChanged finishes the sign-in when we return.
        if (
          code === 'auth/popup-blocked' ||
          code === 'auth/operation-not-supported-in-this-environment' ||
          code === 'auth/web-storage-unsupported'
        ) {
          try {
            await signInWithRedirect(auth, provider);
            return; // page navigates away
          } catch (redirectErr) {
            console.error(redirectErr);
          }
        }
        console.error(err);
        setError(t('auth.errors.unexpected'));
      }
    } finally {
      setBusy(false);
    }
  }, [t]);

  const submit = useCallback(async () => {
    setError(null);
    // The tabbed input pins the kind; we still validate the contents
    // (E.164 / RFC-5322ish) before the API round-trip so the user gets
    // an immediate "that's not a phone number" cue if they typed nothing
    // or the country picker is sitting at a bare dial code.
    const raw = identity.value.trim();
    let req: SendOtpRequest = {};
    if (identity.kind === 'phone' && isPhoneLike(raw)) req = { phone: raw };
    else if (identity.kind === 'email' && isEmailLike(raw.toLowerCase()))
      req = { email: raw.toLowerCase() };
    if (!req.phone && !req.email) {
      setError(
        t(
          EMAIL_OTP_ENABLED
            ? 'auth.errors.invalidIdentity'
            : 'auth.errors.invalidIdentityPhoneOnly',
        ),
      );
      return;
    }
    setBusy(true);
    try {
      const res = await authApi.sendOtp(req);
      navigate('/verify', {
        // Forward `debugCode` so VerifyOtp can auto-fill + auto-submit
        // when the api echoed a code back (Iran admin bypass, non-prod).
        state: { phone: req.phone, email: req.email, debugCode: res.debugCode },
      });
    } catch (err) {
      console.error(err);
      // 429/409/503 each get a distinct, actionable message — notably
      // email OTP 503s when the mail channel isn't live in that env.
      setError(t(otpErrorKey(err, 'auth.errors.sendFailed')));
    } finally {
      setBusy(false);
    }
  }, [identity, navigate, t]);

  return (
    <main className="relative mx-auto flex min-h-dvh max-w-sm flex-col px-6 pb-10 pt-4">
      {/* Slim top row: locale switcher alone, end-aligned — it shares no
          row with the logo, so they can never collide (Peyman round-2). */}
      <header className="flex justify-end">
        <LocaleSwitcher />
      </header>

      {/* Centered brand + title, per the final sign-in mock: lockup
          above "Welcome!", both centered. */}
      <section className="mt-10 flex flex-col items-center gap-6 text-center">
        <img src="/brand/mycity-lockup.svg" alt="My City" className="h-16 w-auto dark:hidden" />
        <img
          src="/brand/mycity-lockup-dark.svg"
          alt=""
          aria-hidden
          className="hidden h-16 w-auto dark:block"
        />
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-extrabold tracking-tight text-primary">
            {t('welcome.title')}
          </h1>
          <p className="text-base text-muted">{t('welcome.tagline')}</p>
        </div>
      </section>

      {/* Auth controls — mock order: identity + Continue first, then
          the divider, then the federated option. */}
      <section className="mt-10 flex flex-1 flex-col gap-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="flex flex-col gap-3"
        >
          <PhoneEmailInput
            value={identity}
            onChange={setIdentity}
            error={error ?? undefined}
            disabled={busy}
          />
          <button
            type="submit"
            disabled={busy || !identity.value}
            className={cn(
              'h-12.5 w-full rounded-button bg-primary font-semibold text-primary-foreground',
              'transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {busy ? t('common.loading') : t('welcome.cta.continue')}
          </button>
        </form>

        <div className="flex items-center gap-3 text-sm text-muted">
          <div className="h-px flex-1 bg-border" />
          <span>{t('welcome.cta.or')}</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Google sign-in — Firebase Web SDK signInWithPopup. */}
        <button
          type="button"
          onClick={() => void signInWithGoogle()}
          disabled={busy}
          className={cn(
            'flex h-12.5 w-full items-center justify-center gap-3 rounded-button border border-border bg-card font-semibold text-foreground',
            'transition hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {t('welcome.cta.google')}
        </button>
      </section>
    </main>
  );
}
